import { ISelectServiceHook } from '@models';
import { GarageService, PagedSelectServiceRequest, Service } from '@services';
import axios from 'axios';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const PAGE_SIZE = 20;

interface IProps {
  enabled?: boolean;
  fixedOnly?: boolean;
  showMyServicesOnly?: boolean;
}

export const useSelectServices = ({
  enabled = true,
  fixedOnly,
  showMyServicesOnly,
}: IProps): ISelectServiceHook => {
  const [services, setServices] = useState<Service[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [skipCount, setSkipCount] = useState(0);
  const [keyword, setKeyword] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);

  const cancelRef = useRef<{ cancel: () => void } | null>(null);
  const requestIdRef = useRef(0);

  const fetchPage = useCallback(
    async (input: { skip: number; search: string | undefined; append: boolean }) => {
      cancelRef.current?.cancel();
      const source = axios.CancelToken.source();
      cancelRef.current = source;
      requestIdRef.current += 1;
      const requestId = requestIdRef.current;

      setLoading(true);

      const request: PagedSelectServiceRequest = {
        fixedOnly,
        showMyServicesOnly,
        isActive: true,
        normalizedName: input.search,
        maxResultCount: PAGE_SIZE,
        skipCount: input.skip,
      };

      try {
        const result = await GarageService.getAll(request, source.token);

        if (requestId !== requestIdRef.current) return;

        setServices((prev) => {
          if (!input.append) return result.items;
          const seen = new Set(prev.map((s) => s.id));
          const merged = [...prev];
          result.items.forEach((s) => {
            if (!seen.has(s.id)) merged.push(s);
          });
          return merged;
        });
        setTotalCount(result.totalCount);
        setLoading(false);
      } catch (error) {
        if (axios.isCancel(error)) return;
        if (requestId !== requestIdRef.current) return;
        setLoading(false);
      }
    },
    [fixedOnly, showMyServicesOnly]
  );

  useEffect(() => {
    if (!enabled) return;
    fetchPage({ skip: skipCount, search: keyword, append: skipCount > 0 });
  }, [enabled, skipCount, keyword, fetchPage]);

  useEffect(() => {
    return () => {
      cancelRef.current?.cancel();
    };
  }, []);

  const debouncedSetKeyword = useMemo(
    () =>
      debounce((text: string | undefined) => {
        setSkipCount(0);
        setKeyword(text && text.length > 0 ? text : undefined);
      }, 300),
    []
  );

  const handleSearch = useCallback(
    (text: string | undefined) => {
      debouncedSetKeyword(text);
    },
    [debouncedSetKeyword]
  );

  const loadMore = useCallback(() => {
    if (loading) return;
    if (services.length >= totalCount) return;
    setSkipCount((prev) => prev + PAGE_SIZE);
  }, [loading, services.length, totalCount]);

  const reset = useCallback(() => {
    debouncedSetKeyword.cancel();
    cancelRef.current?.cancel();
    setServices([]);
    setTotalCount(0);
    setSkipCount(0);
    setKeyword(undefined);
    setLoading(false);
  }, [debouncedSetKeyword]);

  return {
    services,
    totalCount,
    pageSize: PAGE_SIZE,
    skipCount,
    loading,
    hasMore: services.length < totalCount,
    handleSearch,
    loadMore,
    reset,
  };
};
